import {
  SENKRON_YIGIN_SINIRI,
  senkronKaynakAnahtari,
  type KuyrukKaydi,
  type SenkronSonucu,
} from "@koclukpanel/contracts";
import type { SenkronKuyrugu } from "./kuyruk";
import { gonderimKilidiyle, type GonderimKilidi } from "./gonderim-kilidi";

/**
 * PARTI DUZEYINDE HATA NE ANLAMA GELIYOR — tarama-1024 D2.
 *
 * Sunucu kayit kayit sonuc donduruyor; ama parti BUTUN HALINDE de reddedilebilir
 * (ornek: tek bir kaydin govdesi semaya uymadiginda istek 400 alir). Eskiden her
 * parti hatasi "yanitsiz" sayiliyordu: sayac artmadan hepsi "bekliyor"a donuyordu,
 * yani TEK bozuk kayit her denemede ayni partiyi dusurup kuyrugu KALICI tikiyordu.
 *
 *  · `parti_reddi` — 4xx (asagidakiler haric): sorun partinin ICERIGINDE. Parti
 *    ikiye bolunup yeniden denenir; tek kayda inen red o kaydin deneme sayacini
 *    artirir (MAKS_DENEME'de "hatali"ya duser, gecerli kayitlar gitmeye devam eder).
 *  · `gecici` — 5xx, 408, 429: sunucu su an yanit veremiyor; sayac ARTMAZ ama
 *    kayitlar geri cekilmeyle bekler (30 sn → 2 dk → 10 dk), her tetikte sunucuya
 *    yuklenmesinler.
 *  · `yanitsiz` — ag hatasi, 401/403 ve OTURUM_DEGISTI: verinin sucu degil; eski
 *    davranis aynen (sayac yok, geri cekilme yok, baglanti/oturum gelince hemen).
 */
export type PartiHatasiTuru = "parti_reddi" | "gecici" | "yanitsiz";

export function partiHatasiTuru(hata: unknown): PartiHatasiTuru {
  const durum = (hata as { durum?: unknown } | null)?.durum;
  if (typeof durum !== "number") return "yanitsiz";
  if (durum >= 500 || durum === 408 || durum === 429) return "gecici";
  if (durum === 401 || durum === 403) return "yanitsiz";
  if (durum >= 400) return "parti_reddi";
  return "yanitsiz";
}

/**
 * Bir turda tek kayda inip reddedilen kayit sayisinin ust siniri. Sunucu HER
 * partiyi reddediyorsa (ornek: istemci ile sunucu sozlesmesi ayrismis) bolme her
 * kaydi tek tek dener ve hepsinin sayacini artirirdi; sinir hem istek sayisini hem
 * bir turda "suclanan" kayit sayisini kucuk tutar. Gercek tek bozuk kayit yine bulunur.
 */
export const TUR_BASINA_TEKLI_RED = 3;

const GECICI_MESAJ = "Sunucu şu an yanıt veremiyor; kayıt biraz sonra yeniden gönderilecek.";
const PARTI_REDDI_MESAJ = "Bu kayıt sunucuda kabul edilmedi.";

interface TurDurumu {
  tekliRed: number;
  kabulVar: boolean;
  /** Bu turda bir kaydi reddedilen kaynaklar: ayni kaynagin sonraki kaydi ONUNE gecmesin. */
  engelliKaynaklar: Set<string>;
  dur: boolean;
}

export class KuyrukGondericisi {
  private devamEden: Promise<void> | null = null;
  private yenidenIstendi = false;

  constructor(
    private readonly kuyruk: SenkronKuyrugu,
    private readonly gonder: (
      sahip: string,
      kayitlar: readonly KuyrukKaydi[],
    ) => Promise<SenkronSonucu>,
    private readonly oturumGecerli: () => boolean,
    private readonly kilitle: GonderimKilidi = gonderimKilidiyle,
    private readonly kabulSonrasi: () => Promise<void> = async () => undefined,
    private readonly gonderimOncesi: (gecerli: () => boolean) => Promise<void> = async () =>
      undefined,
  ) {}

  gonderimiDene(): Promise<void> {
    this.yenidenIstendi = true;
    if (this.devamEden) return this.devamEden;
    // F-STV-11 — Promise ilk beklemeden önce atanır; React durumu kilit değildir.
    this.devamEden = Promise.resolve()
      .then(async () => {
        do {
          this.yenidenIstendi = false;
          if (!this.oturumGecerli()) return;
          await this.kilitle(async (kilitGecerli = () => true) => {
            const gecerli = () => kilitGecerli() && this.oturumGecerli();
            if (!gecerli()) return;
            await this.gonderimOncesi(gecerli);
            if (!gecerli()) return;
            await this.kuyruk.yarimKalanlariKurtar();
            const gonderilebilir = await this.kuyruk.gonderilecekler();
            const kayitlar = gonderilebilir.slice(0, SENKRON_YIGIN_SINIRI);
            if (!kayitlar.length || !gecerli()) return;
            const tur: TurDurumu = {
              tekliRed: 0,
              kabulVar: false,
              engelliKaynaklar: new Set(),
              dur: false,
            };
            await this.partiyiGonder(kayitlar, gecerli, tur);
            if (gecerli() && tur.kabulVar) await this.kabulSonrasi();
            if (tur.dur) {
              // Yanitsiz/gecici hata ya da red siniri: bu tur burada biter, tetik bekler.
              this.yenidenIstendi = false;
              return;
            }
            if (gecerli() && gonderilebilir.length > kayitlar.length) {
              // Kaynak engellerini yeniden uygula; ilerlemeyen partiyi hemen yineleme.
              const gonderilen = new Set(kayitlar.map((k) => k.istemciKayitId));
              const sonraki = (await this.kuyruk.gonderilecekler()).slice(0, SENKRON_YIGIN_SINIRI);
              if (sonraki.some((kayit) => !gonderilen.has(kayit.istemciKayitId)))
                this.yenidenIstendi = true;
            }
          });
        } while (this.yenidenIstendi && this.oturumGecerli());
      })
      .finally(() => {
        this.devamEden = null;
        if (this.yenidenIstendi && this.oturumGecerli()) return this.gonderimiDene();
      });
    return this.devamEden;
  }

  /**
   * Partiyi gonderir; parti BUTUN HALINDE reddedilirse ikiye bolup yeniden dener
   * (tarama-1024 D2). Yarilar SIRAYLA gider: kaynak sirasi korunur, bir kaydi
   * reddedilen kaynagin sonraki kayitlari bu turda gonderilmez.
   */
  private async partiyiGonder(
    adaylar: readonly KuyrukKaydi[],
    gecerli: () => boolean,
    tur: TurDurumu,
  ): Promise<void> {
    const kayitlar = adaylar.filter(
      (k) => !tur.engelliKaynaklar.has(senkronKaynakAnahtari(k.islem, k.govde)),
    );
    if (!kayitlar.length || tur.dur || !gecerli()) return;
    const parti = await this.kuyruk.gonderimeBasla(kayitlar);
    if (!gecerli()) return;
    let sonuc: SenkronSonucu;
    try {
      sonuc = await this.gonder(parti.sahipKullaniciId, kayitlar);
    } catch (hata) {
      if (!gecerli()) {
        tur.dur = true;
        return;
      }
      const hataTuru = partiHatasiTuru(hata);
      if (hataTuru === "parti_reddi" && kayitlar.length > 1) {
        // Parti serbest birakilir (sayac artmaz), yarilar kendi partileriyle denenir.
        await this.kuyruk.sonucuIsle(parti, { kabul: [], reddedilen: [] });
        const orta = Math.ceil(kayitlar.length / 2);
        await this.partiyiGonder(kayitlar.slice(0, orta), gecerli, tur);
        await this.partiyiGonder(kayitlar.slice(orta), gecerli, tur);
        return;
      }
      if (hataTuru === "parti_reddi") {
        // Tek kayda indi ve yine reddedildi: sorun bu kayitta. Sayac artar.
        const kayit = kayitlar[0]!;
        const mesaj = (hata as { message?: unknown }).message;
        await this.kuyruk.sonucuIsle(parti, {
          kabul: [],
          reddedilen: [
            {
              istemciKayitId: kayit.istemciKayitId,
              mesaj: typeof mesaj === "string" && mesaj.length > 0 ? mesaj : PARTI_REDDI_MESAJ,
              kalici: false,
              kod: "PARTI_REDDI",
            },
          ],
        });
        tur.engelliKaynaklar.add(senkronKaynakAnahtari(kayit.islem, kayit.govde));
        tur.tekliRed += 1;
        if (tur.tekliRed >= TUR_BASINA_TEKLI_RED) tur.dur = true;
        return;
      }
      if (hataTuru === "gecici") {
        // Sayac artmaz (verinin sucu degil) ama geri cekilme baslar.
        await this.kuyruk.sonucuIsle(parti, {
          kabul: [],
          reddedilen: kayitlar.map((k) => ({
            istemciKayitId: k.istemciKayitId,
            mesaj: GECICI_MESAJ,
            kalici: false,
            kod: "SUNUCU_HATASI",
          })),
        });
        tur.dur = true;
        return;
      }
      // F-STV-11 — OTURUM_DEGISTI dahil yanıtsız parti kalıcı hata sayılmaz.
      await this.kuyruk.sonucuIsle(parti, { kabul: [], reddedilen: [] });
      tur.dur = true;
      return;
    }
    if (!gecerli()) return;
    await this.kuyruk.sonucuIsle(parti, sonuc);
    if (sonuc.kabul.length > 0) tur.kabulVar = true;
    // CX-02 (tarama-1024 D2 devami) — HTTP 200 icindeki GECICI red de kaynagi
    // engeller. Sunucu ayni partide bunu kendisi yapiyor; ama bolunmus partide
    // sonraki yari AYRI istektir ve sunucu oncekini bilmez: [eski A, bozuk B,
    // yeni A] bolundugunde eski A gecici reddedilip yeni A kabul ediliyordu,
    // eski A sonraki denemede yenisini eziyordu. Yanitta hic gecmeyen kayit da
    // gecici sayilir. Kalici red engellemez: o kayit bir daha gitmeyecek, sunucu
    // da ayni partide kalici redden sonra kaynagi bekletmiyor.
    const kabul = new Set(sonuc.kabul);
    const kaliciRed = new Set(
      sonuc.reddedilen.filter((red) => red.kalici).map((red) => red.istemciKayitId),
    );
    for (const kayit of kayitlar) {
      if (kabul.has(kayit.istemciKayitId) || kaliciRed.has(kayit.istemciKayitId)) continue;
      tur.engelliKaynaklar.add(senkronKaynakAnahtari(kayit.islem, kayit.govde));
    }
  }
}
